import { uuid } from "@/lib/uuid";
import { offlineDb } from "@/db/offlineDb";

const KEY_ALGO = {
  name: "ECDSA",
  namedCurve: "P-256"
};
const MAX_FAILED_ATTEMPTS = 5;
const LOCKOUT_MS = 5 * 60 * 1000; // 5 minutes

export async function getDeviceId() {
  let record = await offlineDb.mesh_identity.get("primary");
  if (!record) {
    record = { id: "primary", device_id: uuid() };
    await offlineDb.mesh_identity.put(record);
  }
  return record.device_id;
}

let activeActorSession = null;
export function getActiveActorSession() { return activeActorSession; }
export function setActiveActorSession(session) { activeActorSession = session; }

// The signing key has to outlive a page reload.
//
// It used to live only in the module variable above, with the on-disk copy
// wrapped under the captain's PIN. A refresh, a deep link or the kiosk
// restarting therefore left a signed-in captain who could not sign anything:
// submitEvent threw "No active actor session" and ADD did nothing at all,
// mid-service, until they signed out and back in.
//
// A CryptoKey is structured-cloneable, so IndexedDB can hold the live key
// object itself. The key stored here is NON-EXTRACTABLE: the browser will sign
// with it but will never hand its bytes to script, so persisting it does not
// put key material on disk. What it does mean is that the key is usable on this
// device without re-entering the PIN — the same posture as the session JWT
// already sitting in localStorage, and the right trade for a till that must not
// go dead when a tablet reloads. The PIN-wrapped copy stays: that is what an
// OFFLINE sign-in on a device with no session unwraps.
const ACTOR_SESSION_ID = "active-actor";

async function persistActorSession(session) {
  try {
    await offlineDb.mesh_identity.put({
      id: ACTOR_SESSION_ID,
      employee_id: session.employee_id,
      device_id: session.device_id,
      privateKey: session.privateKey,
      authorization: session.authorization,
    });
  } catch (err) {
    // Never let a persistence failure break the login itself.
    console.warn("[identity] could not persist actor session", err?.message);
  }
}

/** Re-arm the in-memory session from IndexedDB after a reload. */
export async function restoreActorSession() {
  if (activeActorSession) return true;
  try {
    const rec = await offlineDb.mesh_identity.get(ACTOR_SESSION_ID);
    if (!rec || !rec.privateKey || !rec.authorization) return false;
    setActiveActorSession({
      employee_id: rec.employee_id,
      privateKey: rec.privateKey,
      authorization: rec.authorization,
      device_id: rec.device_id,
    });
    return true;
  } catch (err) {
    console.warn("[identity] could not restore actor session", err?.message);
    return false;
  }
}

/** Sign-out: the next captain on this tablet must not inherit the last one's key. */
export async function clearActorSession() {
  activeActorSession = null;
  try {
    await offlineDb.mesh_identity.delete(ACTOR_SESSION_ID);
  } catch { /* nothing to clear */ }
}

// The device-level identity used by the WebRTC transport layer (peerManager.js)
// to authenticate this tablet to peers, independent of which captain is
// currently signed in. The private key is generated non-extractable — it never
// leaves the browser in plaintext, so unlike enrollActorIdentity's PIN-wrapped
// key there is nothing here for a PIN to protect.
export async function getOrGenerateDeviceIdentity(deviceId, token) {
  let record = await offlineDb.mesh_identity.get("primary");
  if (record && record.privateKey && record.publicKeyJwk) {
    return record;
  }

  const device_id = deviceId || (record && record.device_id) || await getDeviceId();

  if (!window.crypto || !window.crypto.subtle) {
    throw new Error("WebCrypto not available! Offline mesh disabled.");
  }

  const keyPair = await window.crypto.subtle.generateKey(KEY_ALGO, false, ["sign", "verify"]);
  const publicKeyJwk = await window.crypto.subtle.exportKey("jwk", keyPair.publicKey);

  record = {
    id: "primary",
    device_id,
    privateKey: keyPair.privateKey,
    publicKeyJwk,
    authorization: (record && record.authorization) || null
  };
  await offlineDb.mesh_identity.put(record);
  return record;
}

export async function saveServerAuthorization(authRecord) {
  const record = await offlineDb.mesh_identity.get("primary");
  if (!record) throw new Error("No device identity to attach authorization to");
  record.authorization = authRecord;
  await offlineDb.mesh_identity.put(record);
  return record;
}

export async function signChallenge(privateKey, challengeString) {
  const encoder = new TextEncoder();
  const data = encoder.encode(challengeString);
  const signature = await window.crypto.subtle.sign({ name: "ECDSA", hash: { name: "SHA-256" } }, privateKey, data);
  return arrayBufferToBase64(signature);
}

async function deriveAesKey(pin, saltBuffer) {
  if (!window.crypto || !window.crypto.subtle) throw new Error("WebCrypto not available");
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(pin),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: saltBuffer,
      iterations: 100000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["wrapKey", "unwrapKey"]
  );
}

export async function enrollActorIdentity(employee_id, pin, token) {
  const device_id = await getDeviceId();
  
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error("WebCrypto not available! Offline mesh disabled.");
  }

  const keyPair = await window.crypto.subtle.generateKey(
    KEY_ALGO,
    true,
    ["sign", "verify"]
  );
  const publicKeyJwk = await window.crypto.subtle.exportKey("jwk", keyPair.publicKey);

  const enrollRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mesh/enroll`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ device_id, public_key_jwk: publicKeyJwk })
  });
  
  if (!enrollRes.ok) throw new Error("Mesh enrollment failed");
  const authRecord = await enrollRes.json();

  const saltBuffer = window.crypto.getRandomValues(new Uint8Array(16));
  const aesKey = await deriveAesKey(pin, saltBuffer);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const wrappedKey = await window.crypto.subtle.wrapKey("jwk", keyPair.privateKey, aesKey, { name: "AES-GCM", iv });

  const record = {
    id: employee_id,
    wrappedKey: arrayBufferToBase64(wrappedKey),
    iv: arrayBufferToBase64(iv),
    salt: arrayBufferToBase64(saltBuffer),
    publicKeyJwk,
    authorization: authRecord,
    failed_attempts: 0,
    lockout_until: null
  };
  await offlineDb.mesh_identity.put(record);

  // Sign with a non-extractable handle on the same key rather than the
  // extractable one generateKey had to produce for wrapKey. Unwrapping our own
  // wrapped copy yields exactly that, and it is what gets persisted below.
  let signingKey = keyPair.privateKey;
  try {
    signingKey = await window.crypto.subtle.unwrapKey(
      "jwk", wrappedKey, aesKey, { name: "AES-GCM", iv }, KEY_ALGO, false, ["sign"]
    );
  } catch {
    // Fall back to the in-memory key; signing still works this session.
  }

  const session = { employee_id, privateKey: signingKey, authorization: authRecord, device_id };
  setActiveActorSession(session);
  await persistActorSession(session);
  return record;
}

export async function unlockActorIdentity(employee_id, pin) {
  const record = await offlineDb.mesh_identity.get(employee_id);
  if (!record) return null;
  const device_id = await getDeviceId();

  if (record.lockout_until && Date.now() < record.lockout_until) {
    throw new Error("Too many failed attempts. Device locked out for 5 minutes.");
  }

  if (!window.crypto || !window.crypto.subtle) {
    throw new Error("WebCrypto not available! Offline mesh disabled.");
  }

  try {
    const saltBuffer = base64ToArrayBuffer(record.salt);
    const aesKey = await deriveAesKey(pin, saltBuffer);
    const iv = base64ToArrayBuffer(record.iv);
    const wrappedKey = base64ToArrayBuffer(record.wrappedKey);
    const privateKey = await window.crypto.subtle.unwrapKey(
      "jwk", wrappedKey, aesKey, { name: "AES-GCM", iv }, KEY_ALGO, false, ["sign"]
    );
    
    record.failed_attempts = 0;
    record.lockout_until = null;
    await offlineDb.mesh_identity.put(record);

    const session = { employee_id, privateKey, authorization: record.authorization, device_id };
    setActiveActorSession(session);
    await persistActorSession(session);
    return record.authorization;
  } catch (err) {
    record.failed_attempts = (record.failed_attempts || 0) + 1;
    if (record.failed_attempts >= MAX_FAILED_ATTEMPTS) {
      record.lockout_until = Date.now() + LOCKOUT_MS;
    }
    await offlineDb.mesh_identity.put(record);
    console.error("Failed to unlock actor identity", err);
    if (record.lockout_until) throw new Error("Too many failed attempts. Device locked out for 5 minutes.");
    return null;
  }
}

export async function signEventPayload(payloadStr) {
  const session = getActiveActorSession();
  if (!session) throw new Error("No active actor session");
  
  // Test escape hatch
  if (session.privateKey === "dummy_private") return "dummy_signature";
  
  const encoder = new TextEncoder();
  const data = encoder.encode(payloadStr);
  const signature = await window.crypto.subtle.sign({ name: "ECDSA", hash: { name: "SHA-256" } }, session.privateKey, data);
  return arrayBufferToBase64(signature);
}

export async function importPublicKey(jwk) {
  return await window.crypto.subtle.importKey("jwk", jwk, KEY_ALGO, true, ["verify"]);
}

export async function verifySignature(publicKey, signatureBase64, payloadStr) {
  const encoder = new TextEncoder();
  const data = encoder.encode(payloadStr);
  const signature = base64ToArrayBuffer(signatureBase64);
  return await window.crypto.subtle.verify({ name: "ECDSA", hash: { name: "SHA-256" } }, publicKey, signature, data);
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) { binary += String.fromCharCode(bytes[i]); }
  return window.btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary_string = window.atob(base64);
  const len = binary_string.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) { bytes[i] = binary_string.charCodeAt(i); }
  return bytes.buffer;
}

function sortKeys(obj) {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(sortKeys);
  const sorted = {};
  Object.keys(obj).sort().forEach(key => { sorted[key] = sortKeys(obj[key]); });
  return sorted;
}

export function getEventSigningPayloadString(event) {
  const payloadStr = JSON.stringify(sortKeys(event.payload || {}));
  return [
    event.event_id,
    event.aggregate_id,
    event.type,
    event.device_id,
    event.actor_id,
    payloadStr
  ].join(":");
}
